import type { SiteLegal } from "./site";
import type { SalePlan } from "./orders";
import { formatTry } from "./site";

/**
 * Ön bilgilendirme formu + mesafeli satış sözleşmesi (6502 s. TKHK ve
 * Mesafeli Sözleşmeler Yönetmeliği). Satıcı kimliği `site:legal`'den, alıcı
 * ve sipariş bilgileri formdan gelir. Aynı üreteç: web (onaydan önce ekranda)
 * ve worker (ödeme onayı e-postasında kalıcı kopya). Metinler Türkçedir.
 *
 * ⚠️ Şablon metindir — satışa açmadan önce bir hukukçuya okutulmalı.
 */

export interface SalesDocInput {
  seller: SiteLegal;
  web: string;
  buyer: { name: string; email: string; phone: string; address: string; taxId?: string };
  order: {
    number?: string;
    plan: SalePlan;
    /** "300 misafire kadar" — `orderGuestLabelTr` (satılan hak). */
    rangeLabel: string;
    amount: number;
    /** YYYY-MM-DD */
    eventDate: string;
    /** YYYY-MM-DD — sipariş/sözleşme tarihi */
    date: string;
    /** Satın alınan limitler (sözleşmede sayıyla yazılır). Yoksa satır çizilmez. */
    limits?: SaleLimits;
    /** Paket adı (özel / kurumsal paket); yoksa `plan`'ın adı (Pro / Max). */
    packageLabel?: string;
  };
  retentionMonths: number;
}

/**
 * Satılan paketin sayıları — sözleşmeye BİREBİR yazılır, satılmış paket bu sayılarla kalır (`ent_*`).
 * SMS hakkı ve gönderim turu 2026-10-17'den beri metinde (yoksa — eski kayıt — o kısım yazılmaz).
 */
export interface SaleLimits {
  guests: number;
  media: number;
  storageBytes: number;
  /** SMS gönderim turu (davet / hatırlatma kampanyası). */
  reminderSends?: number;
  /** SMS hakkı (segment). */
  smsSegments?: number;
}

export interface SalesDoc {
  title: string;
  sections: { title: string; items: string[] }[];
}

const PLAN_LABEL: Record<SalePlan, string> = { pro: "Pro", max: "Max" };

/** Geçersiz/boş tarih (önizlemede alan henüz doldurulmamış) → "—", asla throw etmez. */
const trDate = (iso: string) => {
  const d = new Date(`${iso}T12:00:00Z`);
  return Number.isNaN(d.getTime()) ? "—" : new Intl.DateTimeFormat("tr-TR", { dateStyle: "long", timeZone: "UTC" }).format(d);
};

function sellerLines(s: SiteLegal, web: string): string[] {
  return [
    `Unvan / ad soyad: ${s.name}${s.title ? ` (${s.title})` : ""}`,
    `Adres: ${s.address}`,
    ...(s.phone ? [`Telefon: ${s.phone}`] : []),
    `E-posta: ${s.email}`,
    ...(s.kep ? [`KEP: ${s.kep}`] : []),
    ...(s.taxOffice || s.taxId ? [`Vergi dairesi / no: ${[s.taxOffice, s.taxId].filter(Boolean).join(" / ")}`] : []),
    ...(s.mersis ? [`MERSİS no: ${s.mersis}`] : []),
    `Web: ${web}`,
  ];
}

function buyerLines(b: SalesDocInput["buyer"]): string[] {
  return [
    `Ad soyad / unvan: ${b.name}`,
    `Adres: ${b.address}`,
    `Telefon: ${b.phone}`,
    `E-posta: ${b.email}`,
    ...(b.taxId ? [`TCKN / VKN: ${b.taxId}`] : []),
  ];
}

const nfTr = new Intl.NumberFormat("tr-TR", { maximumFractionDigits: 1 });
const storageTr = (bytes: number) =>
  bytes >= 1024 ** 3 ? `${nfTr.format(bytes / 1024 ** 3)} GB` : `${nfTr.format(bytes / 1024 ** 2)} MB`;

function limitsLine(l: SaleLimits): string {
  const sms = typeof l.smsSegments === "number" && typeof l.reminderSends === "number";
  return sms
    ? `Bu etkinlik için sınırlar: en fazla ${nfTr.format(l.guests)} misafir, ${nfTr.format(l.media)} fotoğraf/video, ${storageTr(l.storageBytes)} depolama, ${nfTr.format(l.smsSegments!)} SMS (segment) gönderim hakkı ve ${nfTr.format(l.reminderSends!)} SMS gönderim turu (davet / hatırlatma).`
    : `Bu etkinlik için sınırlar: en fazla ${nfTr.format(l.guests)} misafir, ${nfTr.format(l.media)} fotoğraf/video ve ${storageTr(l.storageBytes)} depolama.`;
}

function serviceLines(i: SalesDocInput): string[] {
  const price = formatTry(i.order.amount, "tr-TR");
  const l = i.order.limits;
  return [
    `Hizmet: ${i.order.packageLabel ?? PLAN_LABEL[i.order.plan]} paketi — ${i.order.rangeLabel} için tek etkinliklik dijital düğün fotoğraf/video paylaşım hizmeti.`,
    ...(l ? [limitsLine(l)] : []),
    "Kapsam: QR kod ile misafir katılımı, fotoğraf ve video yükleme, yayından önce onay (moderasyon), çevrim içi albüm, salondaki ekranda canlı gösterim (Sahne), LCV toplama ve onaylı içeriğin ZIP olarak indirilmesi.",
    `Etkinlik tarihi: ${trDate(i.order.eventDate)}. Albüm, etkinlikten sonra en az ${i.retentionMonths} ay erişilebilir kalır.`,
    `Toplam bedel: ${price} (tüm vergiler dahil, tek seferlik). Teslimat/kargo masrafı yoktur.`,
  ];
}

const paymentLines = [
  "Ödeme, sipariş sonrasında e-postayla iletilen banka hesabına havale/EFT ile yapılır; açıklamaya sipariş numarası yazılır.",
  "Hizmet, ödemenin hesaba geçmesinden itibaren en geç 1 iş günü içinde, alıcının e-posta adresiyle organizatör hesabının açılması (hesap varsa o hesabın kullanılması) ve paketin bu etkinliğin düğününe tanımlanması ile ifa edilmeye başlanır; giriş bilgisi e-postayla bildirilir. Paket tek etkinlik içindir; hesabın diğer düğünlerine uygulanmaz.",
  "Ödeme 7 gün içinde yapılmazsa sipariş iptal edilir.",
];

const withdrawalLines = (sellerEmail: string) => [
  "Alıcı, sözleşmenin kurulduğu (ödemenin yapıldığı) günden itibaren 14 gün içinde herhangi bir gerekçe göstermeksizin ve cezai şart ödemeksizin sözleşmeden cayma hakkına sahiptir.",
  `Cayma bildirimi bu süre içinde ${sellerEmail} adresine yazılı olarak (e-posta ile) yapılır. Bildirimden itibaren en geç 14 gün içinde alınan bedel, ödemede kullanılan yöntemle iade edilir.`,
  "Cayma hakkının kullanılamadığı durum: Mesafeli Sözleşmeler Yönetmeliği m.15/1-(ğ) uyarınca, cayma süresi dolmadan tüketicinin onayıyla ifasına başlanan hizmetlerde (etkinlik için misafir katılımının açılması, içerik yüklenmesi ya da etkinlik tarihinin geçmesi) cayma hakkı kullanılamaz. Hesabın açılması tek başına ifanın başlaması sayılmaz.",
];

const disputeLines = [
  "Şikâyetler satıcının yukarıdaki iletişim adreslerine iletilebilir.",
  "Uyuşmazlıklarda, Ticaret Bakanlığınca her yıl ilan edilen parasal sınırlar dahilinde alıcının yerleşim yerindeki ya da işlemin yapıldığı yerdeki Tüketici Hakem Heyetleri, bu sınırları aşan durumlarda Tüketici Mahkemeleri yetkilidir.",
];

/** Ön bilgilendirme formu (Yönetmelik m.5). */
export function preContractDoc(i: SalesDocInput): SalesDoc {
  return {
    title: "Ön Bilgilendirme Formu",
    sections: [
      { title: "Satıcı", items: sellerLines(i.seller, i.web) },
      { title: "Alıcı", items: buyerLines(i.buyer) },
      { title: "Hizmetin temel nitelikleri ve bedeli", items: serviceLines(i) },
      { title: "Ödeme ve ifa", items: paymentLines },
      { title: "Cayma hakkı", items: withdrawalLines(i.seller.email) },
      { title: "Şikâyet ve itirazlar", items: disputeLines },
      {
        title: "Diğer",
        items: [
          "Kişisel veriler KVKK aydınlatma metnine uygun işlenir; sipariş ve fatura kayıtları vergi mevzuatı gereği 10 yıl saklanır.",
          `Bu form, sipariş onayından önce ${trDate(i.order.date)} tarihinde alıcıya sunulmuştur.`,
        ],
      },
    ],
  };
}

/** Mesafeli satış sözleşmesi (Yönetmelik m.6 vd.). */
export function distanceSalesDoc(i: SalesDocInput): SalesDoc {
  return {
    title: "Mesafeli Satış Sözleşmesi",
    sections: [
      {
        title: "1. Taraflar",
        items: [
          "SATICI —",
          ...sellerLines(i.seller, i.web),
          "ALICI —",
          ...buyerLines(i.buyer),
        ],
      },
      {
        title: "2. Konu",
        items: [
          `İşbu sözleşmenin konusu, alıcının ${i.web} üzerinden${i.order.number ? ` ${i.order.number} numaralı siparişle` : ""} satın aldığı aşağıdaki hizmetin satışı ve ifasına ilişkin tarafların hak ve yükümlülükleridir.`,
          ...serviceLines(i),
        ],
      },
      { title: "3. Ödeme ve ifa", items: paymentLines },
      {
        title: "4. Tarafların yükümlülükleri",
        items: [
          "Satıcı, hizmeti Kullanım Koşulları’nda ve bu sözleşmede belirtilen nitelikte ve sürede sunar; hizmetin sürekliliği için makul teknik önlemleri alır.",
          "Alıcı, platformu Kullanım Koşulları’na uygun kullanır; misafir bilgileri ve paylaşılan içerik için gerekli izinlere sahip olmakla yükümlüdür.",
        ],
      },
      { title: "5. Cayma hakkı", items: withdrawalLines(i.seller.email) },
      { title: "6. Uyuşmazlıkların çözümü", items: disputeLines },
      {
        title: "7. Yürürlük",
        items: [
          "Alıcı, ön bilgilendirme formunu ve bu sözleşmeyi okuyup elektronik ortamda onayladığını kabul eder. Sözleşme, ödemenin satıcı hesabına geçmesiyle kurulur.",
          `Sözleşme tarihi: ${trDate(i.order.date)}.`,
        ],
      },
    ],
  };
}

/** Düz metin (e-posta yedeği / kayıt). */
export function salesDocToText(doc: SalesDoc): string {
  return [doc.title.toLocaleUpperCase("tr-TR"), "", ...doc.sections.flatMap((s) => [s.title, ...s.items.map((it) => `  ${it}`), ""])].join("\n");
}

/**
 * Sözleşme kaydının metni — checkout'ta alıcının onayladığı ve sipariş anında
 * deftere (`sales_ledger`) yazılan metin AYNI üreteçten. Özeti (`sha256Hex`)
 * checkout gönderir; sunucu kendi ürettiğiyle karşılaştırır.
 */
export function salesDocsText(docs: SalesDoc[]): string {
  return docs.map(salesDocToText).join("\n");
}

/** Türkiye takvim günü (YYYY-MM-DD) — sözleşme tarihi tarayıcıda ve sunucuda aynı gün. */
export function istanbulDate(d: Date = new Date()): string {
  return new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/Istanbul", year: "numeric", month: "2-digit", day: "2-digit" }).format(d);
}
